import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * HtmlPreviewPanel — CrashClaw SEND_HTML 预览卡片
 *
 * Bridge 识别助手消息末尾 SEND_HTML:"/path/to/file.html" 后读取文件，
 * 通过 CRASHCLAW_HTML_PREVIEW 事件下发 HTML 内容，本组件以 iframe 渲染为网页样式。
 *
 * 支持：
 *   - 折叠/展开预览
 *   - 弹窗全屏查看
 *   - 复制 HTML 内容
 *   - 导出下载为 .html 文件
 */
import { useMemo, useState } from 'react';
import { FileOutlined, RightOutlined, DownOutlined, ExclamationCircleOutlined, ExpandOutlined, CopyOutlined, DownloadOutlined, } from '@ant-design/icons';
import { Button, Modal, Tooltip, message } from 'antd';
// @ts-ignore - SCSS module
import scss from './AiDrawer.scss';
import { ze } from 'utils/zhEn';
const HtmlPreviewPanel = ({ htmlPreview }) => {
    const [expanded, setExpanded] = useState(true);
    const [hovered, setHovered] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const title = htmlPreview.title || htmlPreview.filePath || ze('HTML 预览', 'HTML Preview');
    const hasHtml = !!htmlPreview.html && !htmlPreview.error;
    const canExpand = hasHtml;
    const iframeSrcDoc = useMemo(() => htmlPreview.html || '', [htmlPreview.html]);
    const handleCopy = (e) => {
        e.stopPropagation();
        if (!htmlPreview.html)
            return;
        navigator.clipboard.writeText(htmlPreview.html).then(() => message.success(ze('已复制到剪贴板', 'Copied to clipboard')), () => message.error(ze('复制失败', 'Copy failed')));
    };
    const handleExport = (e) => {
        e.stopPropagation();
        if (!htmlPreview.html)
            return;
        const blob = new Blob([htmlPreview.html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = htmlPreview.filePath
            ? htmlPreview.filePath.split('/').pop() || 'report.html'
            : 'report.html';
        a.click();
        URL.revokeObjectURL(url);
        message.success(ze('导出成功', 'Exported'));
    };
    const headerClassName = [
        scss.clawHtmlPreviewHeader,
        expanded ? scss.clawHtmlPreviewHeaderExpanded : '',
    ].filter(Boolean).join(' ');
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: scss.clawHtmlPreview, children: [_jsxs("div", { className: headerClassName, style: hovered ? { background: '#efeafb', borderColor: '#ddd6f3' } : undefined, onClick: () => canExpand && setExpanded(!expanded), onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx(FileOutlined, { className: scss.clawHtmlPreviewIcon }), _jsx("span", { className: scss.clawHtmlPreviewTitle, children: title }), htmlPreview.error ? (_jsxs("span", { className: `${scss.clawHtmlPreviewStatus} ${scss.clawHtmlPreviewStatusError}`, children: [_jsx(ExclamationCircleOutlined, { style: { fontSize: 10 } }), ze('加载失败', 'Failed')] })) : (_jsx("span", { className: `${scss.clawHtmlPreviewStatus} ${scss.clawHtmlPreviewStatusReady}`, children: ze('已就绪', 'Ready') })), canExpand && (_jsxs("span", { className: scss.clawHtmlPreviewActions, onClick: (e) => e.stopPropagation(), children: [_jsx(Tooltip, { title: ze('全屏查看', 'Fullscreen'), children: _jsx(Button, { type: "text", size: "small", icon: _jsx(ExpandOutlined, {}), onClick: () => setModalOpen(true) }) }), _jsx(Tooltip, { title: ze('复制', 'Copy'), children: _jsx(Button, { type: "text", size: "small", icon: _jsx(CopyOutlined, {}), onClick: handleCopy }) }), _jsx(Tooltip, { title: ze('导出', 'Export'), children: _jsx(Button, { type: "text", size: "small", icon: _jsx(DownloadOutlined, {}), onClick: handleExport }) })] })), canExpand && (expanded
                                ? _jsx(DownOutlined, { className: scss.clawHtmlPreviewChevron })
                                : _jsx(RightOutlined, { className: scss.clawHtmlPreviewChevron }))] }), expanded && (_jsx("div", { className: scss.clawHtmlPreviewBody, children: htmlPreview.error ? (_jsxs("div", { className: scss.clawHtmlPreviewError, children: [_jsx("div", { className: scss.clawHtmlPreviewErrorPath, children: htmlPreview.filePath }), _jsx("div", { children: htmlPreview.error })] })) : hasHtml ? (_jsx("iframe", { className: scss.clawHtmlPreviewFrame, title: title, srcDoc: iframeSrcDoc, sandbox: "allow-same-origin" })) : null }))] }), _jsx(Modal, { title: title, open: modalOpen, onCancel: () => setModalOpen(false), footer: null, width: "90%", style: { top: 20 }, bodyStyle: { padding: 0, height: 'calc(100vh - 120px)' }, children: hasHtml && (_jsx("iframe", { srcDoc: iframeSrcDoc, title: title, sandbox: "allow-same-origin", style: { width: '100%', height: '100%', border: 'none' } })) })] }));
};
export default HtmlPreviewPanel;
